'use client';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '@ui/lib';
import { useState } from 'react';

export interface ToggleProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
}

export const Toggle = ({
  checked: controlledChecked,
  defaultChecked = false,
  onChange,
  disabled = false,
  className = '',
  id,
  'aria-label': ariaLabel,
}: ToggleProps) => {
  const [uncontrolledChecked, setUncontrolledChecked] =
    useState(defaultChecked);
  const isControlled = controlledChecked !== undefined;
  const checked = isControlled ? controlledChecked : uncontrolledChecked;

  const handleCheckedChange = (newChecked: boolean) => {
    if (!isControlled) setUncontrolledChecked(newChecked);
    onChange?.(newChecked); // Emit the change up
  };

  return (
    <div className="flex items-center justify-center">
      <SwitchPrimitive.Root
        checked={checked}
        onCheckedChange={handleCheckedChange}
        disabled={disabled}
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        className={cn(
          'relative inline-block h-6 w-10 rounded-full transition-colors duration-200 ease-in-out',
          'data-[state=checked]:bg-primary',
          // Intentionally using on-primary-surface-subdued  as background.
          'data-[state=unchecked]:bg-on-primary-surface-subdued',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          className,
        )}
        id={id}
        aria-label={ariaLabel}
      >
        <SwitchPrimitive.Thumb
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          className={cn(
            'size-5 pointer-events-none block rounded-full bg-primary-surface shadow-line transition-transform duration-200 ease-in-out',
            'data-[state=checked]:translate-x-[18px]',
            'data-[state=unchecked]:translate-x-xxs',
          )}
        />
      </SwitchPrimitive.Root>
    </div>
  );
};
